'use client';

import { FC, useLayoutEffect, useRef } from 'react';

import { PortfolioHeroDto } from '@/components/CaseStudiesPage/types';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import AnimatedWavyBackground from '@/components/shared/AnimatedWavyBackground';
import { Typography } from '@/components/shared/Typography';
import { breadcrumbTrails } from '@/lib/breadcrumbs';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: PortfolioHeroDto;
}

const CaseStudiesHero: FC<Props> = ({ data }) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const breadcrumbsRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) return;

		const breadcrumbs = breadcrumbsRef.current;
		const title = wrapper.querySelector(`.${css.title}`);

		const ctx = gsap.context(() => {
			const tl = gsap.timeline({
				defaults: { ease: 'power3.out', duration: 1.2 },
				scrollTrigger: {
					trigger: wrapper,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			gsap.set(title, {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			if (breadcrumbs)
				gsap.set(breadcrumbs, {
					opacity: 0,
					y: 40,
					filter: 'blur(10px)'
				});

			tl.to(
				title,
				{
					opacity: 1,
					y: 0,
					filter: 'blur(0px)',
					duration: 1.1,
					ease: 'power3.out'
				},
				0
			);

			if (breadcrumbs)
				tl.to(
					breadcrumbs,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				);
		}, wrapper);

		return () => ctx.revert();
	}, []);

	return (
		<section ref={wrapperRef} className={css.wrapper}>
			<div className={css.content}>
				<PageBreadcrumbs
					ref={breadcrumbsRef}
					items={breadcrumbTrails.caseStudiesLobby()}
					withContainer={false}
					wrapperClassName={css.breadcrumbsWrap}
					className={css.breadcrumbs}
				/>
				<Typography uppercase className={css.title} variant="h1" tag="h1">
					{data.slogan}
				</Typography>
			</div>

			<AnimatedWavyBackground />
		</section>
	);
};

export default CaseStudiesHero;
